import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Mail, AlertCircle, CheckCircle, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailTab = ({ onMessage }) => {
  const [emailStatus, setEmailStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ subject: '', content: '', recipient_group: 'all' });
  const [sending, setSending] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/email/status`, { withCredentials: true }),
        axios.get(`${API}/admin/email/logs`, { withCredentials: true })
      ]);
      setEmailStatus(statusRes.data);
      setLogs(logsRes.data);
    } catch (e) {
      console.error('email fetch error', e);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email/send`,
        { subject: form.subject, content: form.content, recipient_group: form.recipient_group, selected_user_ids: [] },
        { withCredentials: true }
      );
      onMessage(`Email processed: ${data.sent}/${data.total} sent${data.failed > 0 ? `, ${data.failed} failed` : ''}`);
      setForm({ subject: '', content: '', recipient_group: 'all' });
      fetchAll();
    } catch (err) {
      onMessage(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const sendMonthly = async () => {
    if (!window.confirm('Send the monthly update email to ALL supporters?')) return;
    setMonthlyLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/monthly-update`, {}, { withCredentials: true });
      onMessage(`Monthly update: ${data.sent}/${data.total} sent`);
      fetchAll();
    } catch (err) {
      onMessage(err.response?.data?.detail || 'Failed to send');
    } finally {
      setMonthlyLoading(false);
    }
  };

  return (
    <div data-testid="admin-email-tab" className="space-y-6">
      {/* SMTP Status Banner */}
      {emailStatus && !emailStatus.configured && (
        <div data-testid="email-not-configured-banner" className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold mb-1">SMTP not configured</p>
            <p>{emailStatus.note}</p>
            <p className="mt-2 font-mono text-xs bg-yellow-100 p-2 rounded">GMAIL_APP_PASSWORD="your-16-char-app-password"</p>
          </div>
        </div>
      )}
      {emailStatus && emailStatus.configured && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-sm text-slate-700">
            SMTP configured. Sending from <strong>{emailStatus.from_email}</strong>
          </p>
        </div>
      )}

      {/* Compose Email */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" /> Compose Email
        </h2>
        <form data-testid="email-compose-form" onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Recipient Group</label>
            <select
              data-testid="email-recipient-group-select"
              value={form.recipient_group}
              onChange={(e) => setForm({ ...form, recipient_group: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Supporters</option>
              <option value="active">Active Supporters</option>
              <option value="pledged">Pledged Supporters</option>
              <option value="prayer">Prayer Supporters</option>
              <option value="norwegian">Norwegian Supporters (NO)</option>
              <option value="english">English Supporters (non-NO)</option>
            </select>
          </div>
          <input
            data-testid="email-subject-input"
            type="text"
            placeholder="Subject"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            data-testid="email-content-input"
            placeholder="Email content (HTML supported, e.g., <p>Hello</p>)"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            required
            rows="8"
            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <button
            data-testid="email-send-btn"
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </form>
      </div>

      {/* Monthly Update */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" /> Monthly Update Email
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Sends a pre-formatted email to all supporters with latest journey posts, support progress,
          your encouragement message, and Wycliffe links.
        </p>
        <button
          data-testid="send-monthly-update-btn"
          onClick={sendMonthly}
          disabled={monthlyLoading}
          className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          {monthlyLoading ? 'Sending...' : 'Send Monthly Update Now'}
        </button>
      </div>

      {/* Email Logs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Sent Emails History</h2>
        {logs.length === 0 ? (
          <p className="text-slate-600">No emails sent yet.</p>
        ) : (
          <div data-testid="email-logs-list" className="space-y-3">
            {logs.map(log => (
              <div key={log.id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                  <h4 className="font-semibold text-slate-900">{log.subject}</h4>
                  <span className="text-xs text-slate-500">{new Date(log.sent_at).toLocaleString()}</span>
                </div>
                <div className="flex gap-3 text-xs text-slate-600 flex-wrap">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">to: {log.recipient_group}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">sent: {log.sent_count}</span>
                  {log.failed_count > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">failed: {log.failed_count}</span>
                  )}
                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full">total: {log.recipient_count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
